import React, { useState, useEffect, Fragment, useMemo } from 'react';
import { useSelector } from 'react-redux';
import cx from 'classnames';
import filesize from 'filesize';
import { useNewAssetPreview } from '../../../../hooks/useNewAssetPreview';
import Loader from '../../../utils/Loader';
import { getAssetDownloadUrl } from '../../../../utils/assets-utility';
import { getDocumentFileIcon, getExternalLinkIcon, getFileExtension, isDocumentFile, isUrlAsset } from '../../../../helpers';
import { getFileName, logAssetDownload } from '../../ItemDataCategoryTable/ItemDataCategoryTableUtils';
import { selectors } from '../../../../reducers/appReducer';
import noPhotoImg from '../../../../images/no-photo.png';

const ArticleDigitalAssetModalGallery = ({ assets, onClose, startingIndex, articleDetails, locale = 'it_IT' }) => {
    const [currentIndex, setCurrentIndex] = useState(startingIndex);

    // console.log('assets from ArticleDigitalAssetModalGallery', assets)
    useEffect(() => {
        setCurrentIndex(startingIndex);
    }, [startingIndex]);

    const getCurrentItem = () => assets[currentIndex];
    const hasNext = () => currentIndex < assets.length - 1;
    const hasPrevious = () => currentIndex - 1 >= 0;

    const nextItem = () => setCurrentIndex(prev => prev + 1);
    const previousItem = () => setCurrentIndex(prev => prev - 1);

    const currentItem = getCurrentItem();
    const hasNextItem = hasNext();
    const hasPreviousItem = hasPrevious();

    // Usa l'immagine di preview invece dell'originale
    const currentAsset = assets[currentIndex]

    const {previewImages, loading} = useNewAssetPreview(currentAsset)

    const assetCurrentIndex = assets[currentIndex]?.id ?? assets[currentIndex]?.url
    const imageSrc = previewImages[assetCurrentIndex] ?? null

    // Determina se l'asset è un URL esterno (non un file con URL)
    // Usa extension per determinare se è un file (pdf, jpg, etc.) o un URL (null o 'url')
    const isExternalUrl = (isUrlAsset(currentItem?.public_url) && !currentItem?.extension) || currentItem?.extension === 'url'
    const ExternalLinkIcon = getExternalLinkIcon()

    const imagePlaceholder = useSelector(state => selectors.getDefaultPlaceholderImage(state)?.imageUrl);
    const imagePlaceholderUrl = imagePlaceholder === './images/no-photo.png' ? noPhotoImg : (imagePlaceholder || noPhotoImg);

    const assetDownloadUrl = isExternalUrl ? currentItem?.public_url : (currentItem?.url?.startsWith('http') ? currentItem?.url : getAssetDownloadUrl(currentItem?.url))

    // Handler per il click sul pulsante download/apri link
    const handleDownloadClick = () => {
        if (articleDetails && currentItem) {
            logAssetDownload(articleDetails, currentItem);
        }
    }

    return (
        <Fragment>
            <div className="modal-header">
                <span
                    className="btn btn-clear float-right"
                    aria-label="Close"
                    onClick={onClose}
                />
                <div className="modal-title h4">{currentItem?.filename ?? getFileName(currentItem?.url) ?? '-'}</div>
            </div>
            <div className="divider" />
            <div className="modal-body text-center">
                {loading && !isExternalUrl && <div
                    style={{minHeight: '150px'}}>
                    <Loader></Loader>
                    Caricamento preview...
                </div>}

                <div style={{ position: 'absolute', top: '40%' }}>
                    <i
                        onClick={hasPreviousItem ? previousItem : () => {}}
                        className={cx('icon icon-4x icon-arrow-left', {
                            'c-hand': hasPreviousItem,
                            'text-gray': !hasPreviousItem
                        })}
                    />
                </div>

                {/* URL esterno - mostra icona link */}
                {isExternalUrl && (
                    <div className="flex items-center justify-center text-center" style={{ minHeight: '150px' }}>
                        <ExternalLinkIcon
                            size={64}
                            className="!text-primary !hover:text-gray-700 transition-colors align-center"
                        />
                    </div>
                )}

                {/* File immagine normale - mostra preview */}
                {!isExternalUrl && !isDocumentFile(currentItem?.filename ?? currentItem?.url) && (
                    <img
                        className="img-responsive"
                        style={{ margin: '0 auto', maxHeight: '60vh' }}
                        src={imageSrc}
                        alt={currentItem?.label}
                        onError={({ currentTarget }) => {
                            currentTarget.onerror = null; // prevents looping
                            currentTarget.src = imagePlaceholderUrl;
                        }}
                    />
                )}

                {/* File documento - mostra icona documento */}
                {!isExternalUrl && isDocumentFile(currentItem?.filename ?? currentItem?.url) && (() => {
                    const Icon = getDocumentFileIcon(currentItem?.filename ?? currentItem?.url)

                    return (
                        <div className="flex items-center justify-center text-center">
                            <Icon
                                size={48}
                                className="!text-primary !hover:text-gray-700 transition-colors align-center"
                            />
                        </div>
                    );
                })()}


                <div style={{ position: 'absolute', top: '40%', right: 0 }}>
                    <i
                        onClick={hasNextItem ? nextItem : () => {}}
                        className={cx('icon icon-4x icon-arrow-right', {
                            'c-hand': hasNextItem,
                            'text-gray': !hasNextItem
                        })}
                    />
                </div>

                <div className="my-2" />
                <div className="my-2 text-center">
                    <strong>{isExternalUrl ? 'URL:' : 'Nome del file:'}</strong> {currentItem?.filename ?? currentItem?.url}
                    {!isExternalUrl && currentItem?.size && (
                        <Fragment>
                            {' '} - <strong>Kb:</strong>{' '}
                            {filesize(currentItem.size, {
                                separator: ',',
                                base: 10
                            })}
                        </Fragment>
                    )}
                </div>

                <a
                    className="btn btn-lg btn-primary btn-rounded whitespace-nowrap"
                    href={assetDownloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleDownloadClick}
                >
                    {isExternalUrl ? (
                        <><ExternalLinkIcon className="inline-block" /> APRI LINK</>
                    ) : (
                        <><i className="icon icon-download" /> DOWNLOAD</>
                    )}
                </a>
            </div>
            <div className="modal-footer" />
        </Fragment>
    );
};

export default ArticleDigitalAssetModalGallery;